<template>
  <div class="pdf-viewer">
    <div class="pdf-toolbar">
      <div class="toolbar-left">
        <span class="doc-title">{{ title }}</span>
      </div>
      <div class="toolbar-center">
        <button :disabled="currentPage <= 1" @click="prevPage" class="btn-page">
          <i class="el-icon-arrow-left"></i>
        </button>
        <span class="page-info">
          <input 
            type="number" 
            v-model.number="inputPage" 
            @keyup.enter="goToPage"
            @blur="goToPage"
            class="page-input"
            :min="1"
            :max="totalPages"
          />
          / {{ totalPages }}
        </span>
        <button :disabled="currentPage >= totalPages" @click="nextPage" class="btn-page">
          <i class="el-icon-arrow-right"></i>
        </button>
      </div>
      <div class="toolbar-right">
        <button @click="zoomOut" class="btn-tool" title="缩小">
          <i class="el-icon-zoom-out"></i>
        </button>
        <span class="zoom-info">{{ Math.round(scale * 100) }}%</span>
        <button @click="zoomIn" class="btn-tool" title="放大">
          <i class="el-icon-zoom-in"></i>
        </button>
        <button @click="fitWidth" class="btn-tool" title="适合宽度">
          <i class="el-icon-full-screen"></i>
        </button>
      </div>
    </div>
    <div class="pdf-content" ref="pdfContent">
      <div class="canvas-wrapper" ref="canvasWrapper">
        <canvas ref="pdfCanvas"></canvas>
      </div>
    </div>
  </div>
</template>

<script>
import * as pdfjsLib from 'pdfjs-dist'

// 设置 worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`

export default {
  name: 'PdfViewer',
  props: {
    url: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: 'PDF文档'
    }
  },
  data() {
    return {
      pdfDoc: null,
      currentPage: 1,
      totalPages: 0,
      scale: 1.5,
      inputPage: 1,
      loading: false,
      rendering: false
    }
  },
  watch: {
    url: {
      handler(newUrl) {
        if (newUrl) {
          this.loadPdf()
        }
      },
      immediate: true
    }
  },
  methods: {
    async loadPdf() {
      if (!this.url) return
      
      this.loading = true
      try {
        const loadingTask = pdfjsLib.getDocument({
          url: this.url,
          cMapUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/cmaps/',
          cMapPacked: true
        })
        
        this.pdfDoc = await loadingTask.promise
        this.totalPages = this.pdfDoc.numPages
        this.currentPage = 1
        this.inputPage = 1
        
        this.$nextTick(() => {
          this.renderPage()
        })
      } catch (error) {
        console.error('加载PDF失败:', error)
        this.$message.error('加载PDF失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    
    async renderPage() {
      if (!this.pdfDoc || this.rendering) return
      
      this.rendering = true
      try {
        const page = await this.pdfDoc.getPage(this.currentPage)
        const canvas = this.$refs.pdfCanvas
        const context = canvas.getContext('2d')
        
        // 根据容器宽度调整scale
        const containerWidth = this.$refs.pdfContent.clientWidth - 40
        const viewport = page.getViewport({ scale: this.scale })
        
        // 如果视口宽度大于容器宽度，调整scale
        let renderScale = this.scale
        if (viewport.width > containerWidth) {
          renderScale = (containerWidth / viewport.width) * this.scale
        }
        
        const renderViewport = page.getViewport({ scale: renderScale })
        
        canvas.height = renderViewport.height
        canvas.width = renderViewport.width
        
        const renderContext = {
          canvasContext: context,
          viewport: renderViewport
        }
        
        await page.render(renderContext).promise
      } catch (error) {
        console.error('渲染页面失败:', error)
      } finally {
        this.rendering = false
      }
    },
    
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
        this.inputPage = this.currentPage
        this.renderPage()
      }
    },
    
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
        this.inputPage = this.currentPage
        this.renderPage()
      }
    },
    
    goToPage() {
      let page = parseInt(this.inputPage)
      if (isNaN(page) || page < 1) {
        page = 1
      } else if (page > this.totalPages) {
        page = this.totalPages
      }
      this.inputPage = page
      this.currentPage = page
      this.renderPage()
    },
    
    zoomIn() {
      if (this.scale < 3) {
        this.scale += 0.25
        this.renderPage()
      }
    },
    
    zoomOut() {
      if (this.scale > 0.5) {
        this.scale -= 0.25
        this.renderPage()
      }
    },
    
    fitWidth() {
      if (!this.pdfDoc) return
      
      this.pdfDoc.getPage(this.currentPage).then(page => {
        const containerWidth = this.$refs.pdfContent.clientWidth - 40
        const viewport = page.getViewport({ scale: 1 })
        this.scale = containerWidth / viewport.width
        this.renderPage()
      })
    }
  },
  
  mounted() {
    // 监听窗口大小变化
    window.addEventListener('resize', () => {
      if (this.pdfDoc) {
        this.fitWidth()
      }
    })
  },
  
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  }
}
</script>

<style scoped>
.pdf-viewer {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #525659;
}

.pdf-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: #323639;
  color: #fff;
  font-size: 14px;
  z-index: 10;
}

.toolbar-left {
  flex: 1;
}

.doc-title {
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  display: inline-block;
}

.toolbar-center {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  justify-content: flex-end;
}

.btn-page, .btn-tool {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s;
}

.btn-page:hover:not(:disabled), .btn-tool:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
}

.btn-page:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  display: flex;
  align-items: center;
  gap: 4px;
}

.page-input {
  width: 50px;
  text-align: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  border-radius: 4px;
  padding: 2px 4px;
  font-size: 14px;
}

.page-input:focus {
  outline: none;
  border-color: #409eff;
}

.zoom-info {
  min-width: 45px;
  text-align: center;
}

.pdf-content {
  flex: 1;
  overflow: auto;
  display: flex;
  justify-content: center;
  padding: 20px;
}

.canvas-wrapper {
  display: inline-block;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
}

canvas {
  display: block;
}
</style>
